/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { defineMessages } from 'react-intl';

export const Connections = defineMessages({
  connections: {
    id: 'phone.Connections.connections',
    defaultMessage: 'Connections'
  },
  description: {
    id: 'phone.Connections.description',
    defaultMessage:
      'Give your system a voice to help customers over the phone. You can bring your existing phone number or connect to a service provider.'
  },
  deleteLabel: {
    id: 'phone.Connections.deleteLabel',
    defaultMessage: 'Delete connection'
  },
  generatePhoneNumber: {
    id: 'phone.PhoneQuickSetup.generatePhoneNumber',
    defaultMessage: 'Generate phone number'
  },
  beta: {
    id: 'ContactCenter.beta',
    defaultMessage: 'Beta'
  }
});

export const ContactCenter = defineMessages({
  stepDescription: {
    id: 'ContactCenter.stepDescription',
    defaultMessage: 'Give your system a voice to help customers over the phone.'
  },
  rightArrowLabel: {
    id: 'ContactCenter.rightArrowLabel',
    defaultMessage: 'Right arrow'
  },
  learnMore: {
    id: 'ContactCenter.learnMore',
    defaultMessage: 'Learn more'
  },
  connectToContactCenterTabTitle: {
    id: 'ContactCenter.connectToContactCenterTabTitle',
    defaultMessage: 'Contact center integrations'
  },
  connectToContactCenterTabDescription: {
    id: 'ContactCenter.connectToContactCenterTabDescription',
    defaultMessage:
      'With a service desk connection, your assistant can transfer users to live agents. <link>Learn more</link>'
  },
  connectToContactCenterTitle: {
    id: 'ContactCenter.connectToContactCenterTitle',
    defaultMessage: 'Connect to your {contactCenter} account'
  },
  connectToContactCenterInstruction: {
    id: 'ContactCenter.connectToContactCenterInstruction',
    defaultMessage:
      'Add the account key of the {contactCenter} account that you want to connect to your assistant.'
  },
  connectToContactCenterNote: {
    id: 'ContactCenter.connectToContactCenterNote',
    defaultMessage:
      "Note: You must have administrative access to your organization's {contactCenter} account to complete this."
  },
  connectToContactSetupInformation: {
    id: 'ContactCenter.connectToContactSetupInformation',
    defaultMessage: 'Setup information'
  },
  contactCenterStepDescription: {
    id: 'ContactCenter.contactCenterStepDescription',
    defaultMessage:
      'With a contact center connection, your assistant can transfer users to live agents. <link>Learn more</link>'
  },
  authenticationUrlInputPlaceHolder: {
    id: 'ContactCenter.authenticationUrlInputPlaceHolder',
    defaultMessage: 'http://username:password@example.com/'
  },
  authenticationUrlInputLabel: {
    id: 'ContactCenter.authenticationUrlInputLabel',
    defaultMessage: 'Authentication URL'
  },
  authenticationUrlInputTooltip: {
    id: 'ContactCenter.authenticationUrlInputTooltip',
    defaultMessage: 'The endpoint for access key authentication'
  },
  apiUrlInputPlaceHolder: {
    id: 'ContactCenter.apiUrlInputPlaceHolder',
    defaultMessage: 'https://api.example.com/v1'
  },
  apiUrlInputLabel: {
    id: 'ContactCenter.apiUrlInputLabel',
    defaultMessage: 'API URL'
  },
  apiUrlInputTooltip: {
    id: 'ContactCenter.apiUrlInputTooltip',
    defaultMessage: 'The NICE CXone admin API endpoint'
  },
  contactIdInputPlaceHolder: {
    id: 'ContactCenter.contactIdInputPlaceHolder',
    defaultMessage: 'X-ContactID'
  },
  contactIdInputLabel: {
    id: 'ContactCenter.contactIdInputLabel',
    defaultMessage: 'Contact ID'
  },
  contactIdInputTooltip: {
    id: 'ContactCenter.contactIdInputTooltip',
    defaultMessage: 'The SIP header field name that contains the Contact ID'
  },
  connectToContactCredentials: {
    id: 'ContactCenter.connectToContactCredentials',
    defaultMessage: 'Credentials'
  },
  accessKeyIdInputLabel: {
    id: 'ContactCenter.accessKeyIdInputLabel',
    defaultMessage: 'Access key ID'
  },
  accessKeyIdInputPlaceHolder: {
    id: 'ContactCenter.accessKeyIdInputPlaceHolder',
    defaultMessage: 'Enter {contactCenter} access key ID'
  },
  accessKeySecretInputLabel: {
    id: 'ContactCenter.accessKeySecretInputLabel',
    defaultMessage: 'Access key secret'
  },
  accessKeySecretInputPlaceHolder: {
    id: 'ContactCenter.accessKeyIdInputPlaceHolder',
    defaultMessage: 'Enter {contactCenter} access key secret'
  },
  connectAccountButton: {
    id: 'ContactCenter.connectAccountButton',
    defaultMessage: 'Test connection'
  },
  connected: {
    id: 'ContactCenter.connected',
    defaultMessage: 'Connected'
  },
  connecting: {
    id: 'ContactCenter.connecting',
    defaultMessage: 'Attempting to connect...'
  },
  notConnected: {
    id: 'ContactCenter.notConnected',
    defaultMessage:
      'Unable to connect. Please verify your account information and try again.'
  },
  iconDescription: {
    id: 'ContactCenter.iconDescription',
    defaultMessage: 'Input description'
  },
  instanceArnInputLabel: {
    id: 'ContactCenter.instanceArnInputLabel',
    defaultMessage: 'Amazon Connect Instance ARN'
  },
  instanceArnInputPlaceHolder: {
    id: 'ContactCenter.instanceArnInputPlaceHolder',
    defaultMessage:
      'For example: arn:aws:connect:endpoint:example:instance/asdfghjkl'
  },
  instanceArnInputTooltip: {
    id: 'ContactCenter.instanceArnInputTooltip',
    defaultMessage: 'Find your ARN'
  }
});

export const LiveAgent = defineMessages({
  title: {
    id: 'phone.LiveAgent.title',
    defaultMessage: 'Live agent integrations'
  },
  description: {
    id: 'phone.LiveAgent.description',
    defaultMessage:
      'Set up a service desk connection so your assistant can transfer customers to live agents. Click below to see specific documentation.'
  },
  subtitle: {
    id: 'phone.LiveAgent.subtitle',
    defaultMessage: 'Call and transfer failures'
  },
  messageTooLong: {
    id: 'phone.LiveAgent.messageTooLong',
    defaultMessage: 'Message is too long. The maximum length is 150 characters.'
  },
  sipTargetLabel: {
    id: 'phone.LiveAgent.sipTargetLabel',
    defaultMessage: 'SIP target when a call fails'
  },
  sipTargetPlaceholder: {
    id: 'phone.LiveAgent.sipTargetPlaceholder',
    defaultMessage: 'For example: sip:18001234567@terminate.uri.net'
  },
  sipInvalid: {
    id: 'phone.LiveAgent.sipInvalid',
    defaultMessage: 'SIP URI must start with "sip:"|"sips:"|"tel:"'
  },
  sipUsernameRequired: {
    id: 'phone.LiveAgent.sipUsernameRequired',
    defaultMessage: 'Username is required if using SIP authentication'
  },
  sipPasswordRequired: {
    id: 'phone.LiveAgent.sipPasswordRequired',
    defaultMessage: 'Password is required if using SIP authentication'
  },
  callFailureLabel: {
    id: 'phone.LiveAgent.callFailureLabel',
    defaultMessage: 'Call failure message'
  },
  callFailureDefault: {
    id: 'phone.LiveAgent.callFailureDefault',
    defaultMessage:
      "There's been a problem with the call. You’re being transferred to a live agent now."
  },
  callFailureReplyURL: {
    id: 'phone.LiveAgent.callFailureReplyURL',
    defaultMessage: 'https://example.com/failure_occurred.wav'
  },
  transferFailureLabel: {
    id: 'phone.LiveAgent.transferFailureLabel',
    defaultMessage: 'Transfer failure message'
  },
  transferFailureDefault: {
    id: 'phone.LiveAgent.transferFailureDefault',
    defaultMessage:
      'Your call could not be transferred to a live agent. Please try again later. Thank you. Goodbye.'
  },
  transferTargetInvalid: {
    id: 'phone.LiveAgent.transferTargetInvalid',
    defaultMessage:
      'Should be a valid SIP (sip|sips:+18004441212@company.com) or TEL URI (tel:+18004441212)',
    description: 'Used to explain appropriate URI patterns accepted'
  },
  errorHandlingReplyURL: {
    id: 'phone.LiveAgent.errorHandlingReplyURL',
    defaultMessage: 'https://example.com/transfer_failure_occurred.wav'
  },
  disconnectOnFailure: {
    id: 'phone.LiveAgent.disconnectOnFailure',
    defaultMessage: 'Disconnect call on transfer failure'
  }
});

export const SipTrunk = defineMessages({
  title: {
    id: 'phone.SipTrunk.title',
    defaultMessage: 'Bring your own SIP trunk'
  },
  descriptionGenesys: {
    id: 'phone.SipTrunk.descriptionGenesys',
    defaultMessage:
      "Configure a SIP trunk with Genesys using <link>these steps</link>. Copy this SIP address and add it to Genesys's origination configuration."
  },
  descriptionIntelepeer: {
    id: 'phone.SipTrunk.descriptionIntelepeer',
    defaultMessage:
      "Configure a SIP trunk with IntelePeer using <link>these steps</link>. Copy this SIP address and add it to IntelePeer's origination configuration."
  },
  descriptionTwilio: {
    id: 'phone.SipTrunk.descriptionTwilio',
    defaultMessage:
      "Configure a SIP trunk with Twilio using <link>these steps</link>. Copy this SIP address and add it to Twilio's origination configuration."
  },
  descriptionBYO: {
    id: 'phone.SipTrunk.descriptionBYO',
    defaultMessage:
      "To use your own SIP trunk, configure a SIP trunk from our <link>supported external providers</link>. Copy this SIP address and add it to your SIP trunking provider's origination configuration."
  },
  secondaryLabel: {
    id: 'phone.PhoneQuickSetup.byoSecondaryLabel',
    defaultMessage: 'BYO SIP trunk'
  },
  byoDescriptionOne: {
    id: 'phone.PhoneQuickSetup.byoDescriptionOne',
    defaultMessage:
      'Watson Assistant already supports many SIP trunking providers, see the <link>supported list</link>. If your provider is not currently supported, submit a ticket to get yours added quickly.'
  },
  byoDescriptionTwo: {
    id: 'phone.PhoneQuickSetup.byoDescriptionTwo',
    defaultMessage: "To use your own SIP trunk, here's what you need to do:"
  },
  byoOrderedListOne: {
    id: 'phone.PhoneQuickSetup.byoOrderedListOne',
    defaultMessage:
      "Configure a SIP trunk with your provider - this is the virtual line that'll connect your assistant with a phone number. Either use your own telephony's provider, or use one of the following existing systems documented here:"
  },
  intelepeerOption: {
    id: 'phone.PhoneQuickSetup.IntelepeerOption',
    defaultMessage: '<link>IntelePeer</link>'
  },
  genesysOption: {
    id: 'phone.PhoneQuickSetup.genesysOption',
    defaultMessage: '<link>Genesys</link>'
  },
  niceOption: {
    id: 'phone.PhoneQuickSetup.niceOption',
    defaultMessage: 'NICE inContact'
  },
  twilioOption: {
    id: 'phone.PhoneQuickSetup.twilioOption',
    defaultMessage: '<link>Twilio</link>'
  },
  byoOption: {
    id: 'phone.PhoneQuickSetup.byoOption',
    defaultMessage: '<link>Bring your own</link>'
  },
  byoOrderedListTwo: {
    id: 'phone.PhoneQuickSetup.byoOrderedListTwo',
    defaultMessage:
      "Copy this SIP address and add it to your SIP trunking provider's origination configuration."
  },
  sipLabel: {
    id: 'phone.SipTrunk.sipLabel',
    defaultMessage: 'SIP uniform resource identifier (URI)'
  },
  subtitle: {
    id: 'phone.SipTrunk.subtitle',
    defaultMessage: 'SIP trunking integration'
  },
  sipTrunkingLabel: {
    id: 'phone.SipTrunk.sipTrunkingLabel',
    defaultMessage: 'SIP invite headers to extract'
  },
  sipTrunkingPlaceholder: {
    id: 'phone.SipTrunk.sipTrunkingPlaceholder',
    defaultMessage: 'For example: User-to-User, Custom-Header'
  },
  sipTrunkingHelperText: {
    id: 'phone.SipTrunk.sipTrunkingHelperText',
    defaultMessage: 'Enter comma-separated values to extract multiple headers.'
  },
  disableRing: {
    id: 'phone.SipTrunk.disableRing',
    defaultMessage:
      'Disable the ring that callers will hear while the assistant is contacted'
  },
  disableRingTooltip: {
    id: 'phone.SipTrunk.disableRingTooltip',
    defaultMessage:
      'A provisional ringing response is not compatible with all providers.'
  },
  doNotPlaceOnHold: {
    id: 'phone.SipTrunk.doNotPlaceOnHold',
    defaultMessage:
      "Don't place callers on hold while transferring to a live agent"
  },
  doNotPlaceOnHoldTooltip: {
    id: 'phone.SipTrunk.doNotPlaceOnHoldTooltip',
    defaultMessage:
      'Placing callers on hold is not compatible with all providers.'
  },
  moreInformation: {
    id: 'phone.SipTrunk.moreInformation',
    defaultMessage: 'More information'
  }
});
